import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Brain, Lightning, Warning, Heartbeat, TrendUp, Shield,
  CheckCircle, XCircle, CaretRight, CaretDown, Eye,
  ArrowRight, Sparkle, Lightbulb, ChartLineUp, Globe,
  Buildings, Clock, Target, FileText, Plugs, Gear
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// Category styles
const CATEGORY_STYLES = {
  high_risk_incidents: { 
    bg: 'bg-red-500/10', 
    border: 'border-red-500/30', 
    text: 'text-red-400', 
    icon: Warning,
    label: 'High-Risk Incidents'
  },
  active_breaches: { 
    bg: 'bg-amber-500/10', 
    border: 'border-amber-500/30', 
    text: 'text-amber-400', 
    icon: Heartbeat,
    label: 'Active Breaches'
  },
  non_compliance_logs: { 
    bg: 'bg-orange-500/10', 
    border: 'border-orange-500/30', 
    text: 'text-orange-400', 
    icon: XCircle,
    label: 'Non-Compliance Logs'
  },
  industry_trends: { 
    bg: 'bg-blue-500/10', 
    border: 'border-blue-500/30', 
    text: 'text-blue-400', 
    icon: TrendUp,
    label: 'Industry Trends'
  },
  control_gaps: { 
    bg: 'bg-purple-500/10', 
    border: 'border-purple-500/30', 
    text: 'text-purple-400', 
    icon: Shield,
    label: 'Control Gaps'
  }
};

// Insight Card Component
const InsightCard = ({ insight, onExpand, isExpanded }) => {
  const categoryStyle = CATEGORY_STYLES[insight.category] || CATEGORY_STYLES.high_risk_incidents;
  const CategoryIcon = categoryStyle.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-background-surface border rounded-lg overflow-hidden ${categoryStyle.border}`}
    >
      <button
        onClick={() => onExpand(insight.id)}
        className={`w-full p-4 ${categoryStyle.bg} flex items-center justify-between hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-background/50 flex items-center justify-center">
            <CategoryIcon size={20} className={categoryStyle.text} weight="fill" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{insight.title}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${categoryStyle.bg} ${categoryStyle.text}`}>
                {insight.severity || 'Medium'}
              </span>
            </div>
            <p className="text-xs text-text-tertiary mt-0.5">{insight.source || 'NAVI Analysis'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {insight.is_new && (
            <span className="px-2 py-0.5 bg-gold/10 text-gold text-xs rounded">New</span>
          )}
          <CaretDown size={16} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-4 border-t border-border/50">
              {/* Description */}
              <div>
                <div className="text-xs text-text-tertiary uppercase mb-1">Description</div>
                <p className="text-sm text-text-secondary">{insight.description}</p>
              </div>
              
              {/* NAVI Analysis */}
              {insight.navi_analysis && (
                <div className="p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Brain size={16} className="text-purple-400 mt-0.5" />
                    <div>
                      <div className="text-xs font-medium text-purple-400 uppercase mb-1">NAVI Analysis</div>
                      <p className="text-sm text-text-secondary">{insight.navi_analysis}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Audit Recommendations */}
              {insight.audit_recommendations && insight.audit_recommendations.length > 0 && (
                <div>
                  <div className="text-xs text-text-tertiary uppercase mb-2">Audit Recommendations</div>
                  <div className="space-y-2">
                    {insight.audit_recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-background rounded">
                        <Lightbulb size={14} className="text-gold mt-0.5" />
                        <span className="text-xs text-text-secondary">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Related Frameworks */}
              {insight.related_frameworks && insight.related_frameworks.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {insight.related_frameworks.map((framework, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gold/10 text-gold text-xs rounded">
                      {framework}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                <Link
                  to="/risk-log"
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs rounded hover:bg-amber-500/20"
                >
                  <Warning size={12} />
                  Add to Risk Log
                </Link>
                <Link
                  to="/remediation-tracker"
                  className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs rounded hover:bg-emerald-500/20"
                >
                  <CheckCircle size={12} />
                  Create Remediation
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, trend }) => (
  <div className="bg-background-surface border border-border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-text-tertiary uppercase">{label}</span>
      {Icon && <Icon size={18} className={`text-${color}`} />}
    </div>
    <div className="flex items-end justify-between">
      <span className={`text-2xl font-mono font-bold text-${color}`}>{value}</span>
      {trend && (
        <span className={`text-xs ${trend > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
  </div>
);

// Main Component
const AuditIntelligencePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState([]);
  const [expandedInsight, setExpandedInsight] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [integrationsConnected, setIntegrationsConnected] = useState(0);
  
  const fetchData = useCallback(async () => {
    try {
      const [intelligenceRes, integrationsRes] = await Promise.all([
        axios.get(`${API}/audit-intelligence/dashboard`),
        axios.get(`${API}/integrations/configurations`)
      ]);
      
      setStats(intelligenceRes.data.stats || {});
      setInsights(intelligenceRes.data.insights || []);
      
      const connected = (integrationsRes.data.integrations || []).filter(i => i.status === 'connected').length;
      setIntegrationsConnected(connected);
    } catch (error) {
      console.error('Error fetching audit intelligence:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleExpand = (insightId) => {
    setExpandedInsight(expandedInsight === insightId ? null : insightId);
  };
  
  const filteredInsights = selectedCategory === 'all' 
    ? insights 
    : insights.filter(i => i.category === selectedCategory);
  
  // Sample insights for demo (will be replaced by real data from integrations)
  const sampleInsights = [
    {
      id: '1',
      title: 'Ransomware Attack Pattern Detected in SA Financial Sector',
      category: 'industry_trends',
      severity: 'High',
      source: 'SABRIC Alert Feed',
      description: 'Multiple financial institutions in South Africa have reported increased ransomware attempts targeting legacy systems. The attack vector primarily exploits unpatched VPN vulnerabilities.',
      navi_analysis: 'Based on Joint Standard 2 requirements, your organisation should verify that all VPN appliances are patched to the latest firmware versions. Additionally, ensure that your incident response plan includes specific procedures for ransomware scenarios.',
      audit_recommendations: [
        'Review patch management procedures for network appliances',
        'Test ransomware response procedures in your BCP',
        'Verify backup integrity and restoration capabilities',
        'Assess third-party VPN vendor security posture'
      ],
      related_frameworks: ['Joint Standard 2', 'Cybercrimes Act', 'POPIA'],
      is_new: true
    },
    {
      id: '2',
      title: 'POPIA Compliance Gap: Data Subject Request Handling',
      category: 'non_compliance_logs',
      severity: 'Medium',
      source: 'Internal Assessment',
      description: 'Analysis of your data subject access requests shows an average response time of 35 days, exceeding the POPIA requirement of 30 days.',
      navi_analysis: 'This compliance gap could result in regulatory penalties. Consider implementing automated tracking for DSARs and establishing escalation procedures for requests approaching deadline.',
      audit_recommendations: [
        'Implement DSAR tracking system with deadline alerts',
        'Review current DSAR handling procedures',
        'Train relevant staff on POPIA timelines',
        'Establish escalation path for complex requests'
      ],
      related_frameworks: ['POPIA', 'Joint Standard 1'],
      is_new: false
    },
    {
      id: '3',
      title: 'Emerging Threat: Supply Chain Compromise via Software Updates',
      category: 'industry_trends',
      severity: 'Critical',
      source: 'CSIRT Advisory',
      description: 'Global increase in supply chain attacks targeting software update mechanisms. Several SA organisations have been affected by compromised third-party software.',
      navi_analysis: 'Your third-party risk management controls should be reviewed. Focus on vendors with access to your systems and those providing automatic updates. Consider implementing software composition analysis.',
      audit_recommendations: [
        'Audit all third-party software update mechanisms',
        'Review vendor security assessment procedures',
        'Implement network segmentation for update processes',
        'Establish software integrity verification procedures'
      ],
      related_frameworks: ['Joint Standard 2', 'ISO 27001'],
      is_new: true
    }
  ];
  
  const displayInsights = insights.length > 0 ? filteredInsights : sampleInsights;
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
            <span className="text-sm text-text-tertiary">Loading Audit Intelligence...</span>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Breadcrumb */}
        <div className="px-6 pt-4">
          <PageBreadcrumb items={[
            { label: 'Audit Intelligence' }
          ]} />
        </div>

        {/* Header */}
        <div className="px-6 pb-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Brain size={28} className="text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-medium text-text-primary">
                  Audit Intelligence
                </h1>
                <p className="text-sm text-text-secondary flex items-center gap-2">
                  <Lightning size={14} className="text-purple-400" />
                  NAVI-Powered Insights for Auditors
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/settings/integrations"
                className="flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-gold transition-colors"
              >
                <Plugs size={18} />
                {integrationsConnected} Integrations
              </Link>
              <Link
                to="/themes/cybersecurity"
                className="flex items-center gap-2 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
              >
                <Shield size={18} />
                Cyber Dashboard
              </Link>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="p-6 border-b border-border">
          <div className="grid grid-cols-5 gap-4">
            <StatCard
              label="High-Risk Incidents"
              value={stats?.high_risk_incidents || 0}
              icon={Warning}
              color="red-400"
            />
            <StatCard
              label="Active Breaches"
              value={stats?.active_breaches || 0}
              icon={Heartbeat}
              color="amber-400"
            />
            <StatCard
              label="Non-Compliance"
              value={stats?.non_compliance_logs || 1}
              icon={XCircle}
              color="orange-400"
            />
            <StatCard
              label="Industry Trends"
              value={stats?.industry_trends || 2}
              icon={TrendUp}
              color="blue-400"
            />
            <StatCard
              label="Control Gaps"
              value={stats?.control_gaps || 0}
              icon={Shield}
              color="purple-400"
            />
          </div>
        </div>
        
        {/* Integration Notice */}
        {integrationsConnected === 0 && (
          <div className="mx-6 mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plugs size={24} className="text-amber-400" />
              <div>
                <div className="text-sm font-medium text-text-primary">Connect Your Security Tools</div>
                <div className="text-xs text-text-tertiary">
                  Link Azure AD, Sentinel, Defender, Qualys, or ServiceNow for real-time intelligence
                </div>
              </div>
            </div>
            <Link
              to="/settings/integrations"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm rounded hover:bg-amber-600"
            >
              <Gear size={16} />
              Configure Integrations
            </Link>
          </div>
        )}
        
        {/* Category Filter */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                selectedCategory === 'all' 
                  ? 'bg-gold text-gold-foreground' 
                  : 'bg-background border border-border text-text-secondary hover:border-gold/50'
              }`}
            >
              All Insights
            </button>
            {Object.entries(CATEGORY_STYLES).map(([key, style]) => {
              const CategoryIcon = style.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                    selectedCategory === key 
                      ? `${style.bg} ${style.text} ${style.border} border` 
                      : 'bg-background border border-border text-text-secondary hover:border-gold/50'
                  }`}
                >
                  <CategoryIcon size={14} />
                  {style.label}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Insights List */}
        <div className="p-6">
          {displayInsights.length > 0 ? (
            <div className="space-y-4">
              {displayInsights.map((insight) => (
                <InsightCard
                  key={insight.id}
                  insight={insight}
                  onExpand={handleExpand}
                  isExpanded={expandedInsight === insight.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Brain size={48} className="mx-auto mb-4 text-purple-400/30" />
              <h3 className="text-lg font-medium text-text-secondary mb-2">No Insights Yet</h3>
              <p className="text-sm text-text-tertiary max-w-md mx-auto">
                Connect your enterprise security tools to receive AI-powered audit intelligence.
              </p>
              <Link
                to="/settings/integrations"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-500 text-white rounded-sm hover:bg-purple-600"
              >
                <Plugs size={18} />
                Configure Integrations
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditIntelligencePage;
